/**
 * "Batter, step or support?" excavation selector (free tool,
 * /free/excavation-support-selector; SEO plan Workstream E row 1).
 *
 * Pure logic, no React: a visitor's six answers in, the approaches worth
 * discussing with a competent person and the points to raise out. Every word
 * the visitor can see lives here, so the page, the printable record and the
 * safety review read one set of text.
 *
 * Deliberately gives NO angles, depths-as-limits, distances or design values.
 * The depth bands only describe the dig. Every outcome ends with the line that
 * a competent person decides the actual profile and support.
 *
 * Sources (read 31 Oct 2026):
 *  - CDM 2015 regulation 22 on legislation.gov.uk, the legal anchor:
 *    22(1) supports or battering, 22(2) falls into the excavation,
 *    22(3) overloading, 22(4) inspections.
 *  - HSE "Excavations" (hse.gov.uk/construction/safetytopics/excavations.htm):
 *    every sentence in quotation marks below is HSE's own wording.
 *
 * Pinned path by path (all 576 answer combinations) in
 * __tests__/lib/marketing/excavation-selector/selector.test.ts.
 */
import { CDM_REG_22_URL, HSE_EXCAVATIONS_URL } from "./sources";

export type Depth = "shallow" | "medium" | "deep";
export type Ground = "cohesive" | "granular" | "made" | "unknown";
export type Water = "dry" | "wet" | "inflow" | "unknown";
export type Space = "plenty" | "some" | "tight";
export type YesNo = "yes" | "no";

export interface SelectorAnswers {
  depth: Depth;
  ground: Ground;
  water: Water;
  edgeLoads: YesNo;
  structures: YesNo;
  space: Space;
}

export type QuestionId = keyof SelectorAnswers;

export interface Question {
  id: QuestionId;
  label: string;
  hint?: string;
  options: readonly { value: string; label: string }[];
}

export interface Cite {
  label: string;
  url: string;
}

const REG_22 = (paragraph: string): Cite => ({
  label: `CDM 2015 reg 22(${paragraph})`,
  url: CDM_REG_22_URL,
});
const HSE: Cite = { label: "HSE, Excavations", url: HSE_EXCAVATIONS_URL };

export interface Point {
  id: string;
  text: string;
  cite: Cite;
}

// ---------------------------------------------------------------------------
// Questions (exact wording shown to the visitor)
// ---------------------------------------------------------------------------

const YES_NO = [
  { value: "yes", label: "Yes" },
  { value: "no", label: "No" },
] as const;

export const QUESTIONS: readonly Question[] = [
  {
    id: "depth",
    label: "How deep will the excavation be?",
    hint: "These bands only describe the dig. No depth is safe on its own, and none of them is a legal limit.",
    options: [
      { value: "shallow", label: "Shallow: about knee height or less on someone standing in it" },
      { value: "medium", label: "Medium: deeper than knee height, up to head height" },
      { value: "deep", label: "Deep: deeper than head height" },
    ],
  },
  {
    id: "ground",
    label: "What is the ground like?",
    options: [
      { value: "cohesive", label: "Firm clay or similar ground that holds together" },
      { value: "granular", label: "Sand, gravel or other loose, granular ground" },
      { value: "made", label: "Made ground, fill, or ground that has been dug before" },
      { value: "unknown", label: "Mixed, or I don't know yet" },
    ],
  },
  {
    id: "water",
    label: "What about water?",
    options: [
      { value: "dry", label: "Dry, and no heavy rain expected while it is open" },
      { value: "wet", label: "The ground is wet, or heavy rain is expected while it is open" },
      {
        value: "inflow",
        label: "Water is likely to come in: high groundwater, close to a river, lake or the sea, or a leaking pipe",
      },
      { value: "unknown", label: "I don't know yet" },
    ],
  },
  {
    id: "edgeLoads",
    label: "Will spoil, materials, plant or vehicles be near the edge?",
    options: YES_NO,
  },
  {
    id: "structures",
    label: "Is there a building, wall, scaffold footing or road near the excavation?",
    options: YES_NO,
  },
  {
    id: "space",
    label: "How much room is there around the excavation?",
    options: [
      { value: "plenty", label: "Plenty of room on every side" },
      { value: "some", label: "Room on some sides only" },
      { value: "tight", label: "Very little: a boundary, building or road is right next to it" },
    ],
  },
];

// ---------------------------------------------------------------------------
// Approaches
// ---------------------------------------------------------------------------

export type ApproachId = "batter" | "step" | "support";
export type ApproachStatus = "discuss" | "some-sides" | "hard-to-fit" | "unsuited-ground";

export const STATUS_LABELS: Record<ApproachStatus, string> = {
  discuss: "Worth discussing",
  "some-sides": "Worth discussing for the sides with room",
  "hard-to-fit": "Hard to fit in the space you have",
  "unsuited-ground": "Not usually suitable in this ground",
};

export const APPROACH_TITLES: Record<ApproachId, string> = {
  batter: "Batter back the sides",
  step: "Step (bench) the sides",
  support: "Support the sides (trench box, or trench sheets and props)",
};

export interface Approach {
  id: ApproachId;
  title: string;
  status: ApproachStatus;
  points: readonly Point[];
}

export const POINTS = {
  // Approach introductions -------------------------------------------------
  "batter-intro": {
    id: "batter-intro",
    text: "Battering means cutting the sides back to a slope. CDM 2015 names battering, and supports, as ways to stop an excavation collapsing where necessary.",
    cite: REG_22("1"),
  },
  "step-intro": {
    id: "step-intro",
    text: "Stepping (benching) cuts the sides back in a series of steps instead of one slope. Each face still relies on the ground standing at that height.",
    cite: REG_22("1"),
  },
  "support-intro": {
    id: "support-intro",
    text: "HSE says: “Before digging any trench pit, tunnel, or other excavations, decide what temporary support will be required and plan the precautions to be taken.” It also says to make sure the equipment is on site before work starts.",
    cite: HSE,
  },
  // Space ------------------------------------------------------------------
  "cut-back-some-sides": {
    id: "cut-back-some-sides",
    text: "Cutting the sides back takes room around the excavation. You have room on some sides only, so the competent person may combine this with another approach on the other sides.",
    cite: REG_22("1"),
  },
  "cut-back-tight": {
    id: "cut-back-tight",
    text: "Cutting the sides back takes room around the excavation, and you have very little.",
    cite: REG_22("1"),
  },
  "support-tight": {
    id: "support-tight",
    text: "With little room to cut the sides back, support may be the main approach to discuss.",
    cite: REG_22("1"),
  },
  // Ground and water, for cutting back --------------------------------------
  "batter-granular": {
    id: "batter-granular",
    text: "HSE says: “In granular soils, the angle of slope should be less than the natural angle of repose of the material being excavated.”",
    cite: HSE,
  },
  "batter-wet": {
    id: "batter-wet",
    text: "HSE says: “In wet ground a considerably flatter slope will be required.”",
    cite: HSE,
  },
  "batter-ground-unknown": {
    id: "batter-ground-unknown",
    text: "The slope that is safe depends on the ground, so the ground needs to be known before a slope is chosen.",
    cite: REG_22("1"),
  },
  "batter-deep": {
    id: "batter-deep",
    text: "The deeper the dig, the more room a slope takes.",
    cite: REG_22("1"),
  },
  "step-ground": {
    id: "step-ground",
    text: "The points about the ground and water for battering apply to every step too.",
    cite: REG_22("1"),
  },
  "step-loose": {
    id: "step-loose",
    text: "Stepping is not usually suitable in loose, granular or waterlogged ground, which will not hold a stepped face. There the sides need supporting or battering back instead.",
    cite: REG_22("1"),
  },
  "step-deep": {
    id: "step-deep",
    text: "On a deeper dig, steps can reduce the height of any single face. The competent person decides the size of each step.",
    cite: REG_22("1"),
  },
  "support-inflow": {
    id: "support-inflow",
    text: "HSE says: “The supports to the side of the excavation should be designed to control the entry of groundwater and the design should take any additional water loading into account.”",
    cite: HSE,
  },
  // Points to raise ----------------------------------------------------------
  "no-ground-stands": {
    id: "no-ground-stands",
    text: "HSE says: “No ground can be relied upon to stand unsupported in all circumstances.”",
    cite: HSE,
  },
  "depth-shallow": {
    id: "depth-shallow",
    text: "A shallow dig still needs planning. HSE warns that many garden or boundary walls have very shallow foundations which are easily undermined by even small trenches.",
    cite: HSE,
  },
  "depth-weight": {
    id: "depth-weight",
    text: "HSE says: “Depending on conditions, a cubic metre of soil can weigh in excess of 1.5 tonnes.”",
    cite: HSE,
  },
  "depth-deep": {
    id: "depth-deep",
    text: "Ask whether the support or profile needs a temporary works design before digging starts.",
    cite: HSE,
  },
  "ground-info": {
    id: "ground-info",
    text: "Ask for any information about the ground. HSE says commercial clients must give contractors information on ground conditions, underground structures or water courses before work begins.",
    cite: HSE,
  },
  "ground-unknown": {
    id: "ground-unknown",
    text: "Find out what the ground is before the method is chosen.",
    cite: REG_22("1"),
  },
  "water-wet": {
    id: "water-wet",
    text: "Plan for an inspection after heavy rain. Where supports or battering are provided, CDM 2015 requires an inspection after any event likely to have affected the strength or stability of the excavation.",
    cite: REG_22("4"),
  },
  "water-pumping": {
    id: "water-pumping",
    text: "If water will be pumped out, HSE says “the effect of pumping from sumps on the stability of the excavation should be considered.”",
    cite: HSE,
  },
  "water-unknown": {
    id: "water-unknown",
    text: "Find out about groundwater and nearby water courses before the method is chosen.",
    cite: HSE,
  },
  "edge-loads": {
    id: "edge-loads",
    text: "Plan where spoil, materials and plant will go. CDM 2015 requires steps, where necessary, to stop the excavation or the ground next to it being overloaded. HSE says: “Do not park plant and vehicles close to the sides of excavations.”",
    cite: REG_22("3"),
  },
  "edge-clear": {
    id: "edge-clear",
    text: "Agree before digging where spoil, materials and plant will go, so the edge does not get loaded later.",
    cite: REG_22("3"),
  },
  structures: {
    id: "structures",
    text: "HSE says: “Make sure excavations do not undermine the scaffold footings, buried services or the foundations of nearby buildings or walls.” It also says to decide before digging if extra support for the structure is needed, and that a structural engineer’s advice may be required.",
    cite: HSE,
  },
  // Whatever is chosen -------------------------------------------------------
  services: {
    id: "services",
    text: "Find underground services first. HSE says excavation work should not start until steps have been taken to identify and prevent any risk of injury arising from underground services.",
    cite: HSE,
  },
  "falls-in": {
    id: "falls-in",
    text: "Stop people, equipment and material falling into the excavation, for example with barriers at the edge.",
    cite: REG_22("2"),
  },
  inspection: {
    id: "inspection",
    text: "Once supports or battering are in place, no one works in the excavation until a competent person has inspected it and is satisfied it is safe: at the start of each shift, after any event likely to have affected its strength or stability, and after any material falls or is dislodged.",
    cite: REG_22("4"),
  },
} as const satisfies Record<string, Point>;

export type PointId = keyof typeof POINTS;

/** Shown with every result, and printed on the record. */
export const COMPETENT_PERSON_LINE =
  "A competent person decides the actual profile and support for the ground on the day. This tool does not give angles, depths or distances.";
export const NOT_LEGAL_ADVICE = "This is general guidance, not legal advice.";
export const SCOPE_LINE =
  "This tool covers Great Britain, where regulation 22 of CDM 2015 applies. Northern Ireland has its own CDM 2016 regulations.";

export interface SelectorResult {
  approaches: readonly Approach[];
  /** Points to raise with the competent person, from the answers. */
  considerations: readonly Point[];
  /** Points that apply whatever approach is chosen. */
  always: readonly Point[];
}

const ALWAYS: readonly PointId[] = ["services", "falls-in", "inspection"];

function cutBackStatus(space: Space): ApproachStatus {
  if (space === "plenty") return "discuss";
  if (space === "some") return "some-sides";
  return "hard-to-fit";
}

function cutBackSpacePoints(space: Space): PointId[] {
  if (space === "some") return ["cut-back-some-sides"];
  if (space === "tight") return ["cut-back-tight"];
  return [];
}

const pick = (ids: readonly PointId[]): Point[] => ids.map((id) => POINTS[id]);

export function isComplete(answers: Partial<SelectorAnswers>): answers is SelectorAnswers {
  return QUESTIONS.every((question) => answers[question.id] !== undefined);
}

export function selectApproaches(a: SelectorAnswers): SelectorResult {
  const batter: PointId[] = ["batter-intro", ...cutBackSpacePoints(a.space)];
  if (a.ground === "granular") batter.push("batter-granular");
  if (a.ground === "made" || a.ground === "unknown") batter.push("batter-ground-unknown");
  if (a.water === "wet" || a.water === "inflow") batter.push("batter-wet");
  if (a.depth === "deep") batter.push("batter-deep");

  // Stepping in loose, granular or waterlogged ground: the reviewed position
  // of /guides/excavation-safety ("Benching (stepping)"), kept consistent here.
  const looseForSteps = a.ground === "granular" || a.water === "inflow";
  const step: PointId[] = ["step-intro", ...cutBackSpacePoints(a.space)];
  if (looseForSteps) step.push("step-loose");
  else if (a.ground !== "cohesive" || a.water === "wet") step.push("step-ground");
  if (a.depth === "deep") step.push("step-deep");

  const support: PointId[] = ["support-intro"];
  if (a.space === "tight") support.push("support-tight");
  if (a.water === "inflow") support.push("support-inflow");

  const considerations: PointId[] = ["no-ground-stands"];
  if (a.depth === "shallow") considerations.push("depth-shallow");
  else considerations.push("depth-weight");
  if (a.depth === "deep") considerations.push("depth-deep");
  if (a.ground === "made" || a.ground === "unknown") considerations.push("ground-info");
  if (a.ground === "unknown") considerations.push("ground-unknown");
  if (a.water === "wet") considerations.push("water-wet");
  if (a.water === "inflow") considerations.push("water-pumping");
  if (a.water === "unknown") considerations.push("water-unknown");
  considerations.push(a.edgeLoads === "yes" ? "edge-loads" : "edge-clear");
  if (a.structures === "yes") considerations.push("structures");

  return {
    approaches: [
      { id: "batter", title: APPROACH_TITLES.batter, status: cutBackStatus(a.space), points: pick(batter) },
      {
        id: "step",
        title: APPROACH_TITLES.step,
        status: a.space !== "tight" && looseForSteps ? "unsuited-ground" : cutBackStatus(a.space),
        points: pick(step),
      },
      { id: "support", title: APPROACH_TITLES.support, status: "discuss", points: pick(support) },
    ],
    considerations: pick(considerations),
    always: pick(ALWAYS),
  };
}

/** The visitor's answer to one question, as the option label they chose. */
export function answerLabel(question: Question, answers: Partial<SelectorAnswers>): string {
  const value = answers[question.id];
  return question.options.find((option) => option.value === value)?.label ?? "";
}
